import React, { useMemo } from 'react';

import { Section, Grid } from '@gogame/components';
import { withForm, SidePageV2, FormField, FormFieldGroup } from '@gogame/form';

import {
  getCurrentComponent,
  getCurrentValidationSchema,
} from 'scenes/CustomAudiences/LookalikeAudiences/components/LookalikeAudienceForm/Partners';
import {
  loadAccounts,
  loadAdSets,
  loadCampaigns,
  loadPartners,
  loadCredentials,
} from 'scenes/CustomAudiences/LookalikeAudiences/sources/fields';

import ResultFormField from './ResultFormField';
import {
  PARTNER,
  TYPE,
  TARGETING,
  ENTRIES,
  ACCOUNT,
  ADSETS,
  CAMPAIGNS,
  NAME,
  CREDENTIALS,
} from './constants';
import {
  baseCampaignBasedLookalikeTargetingValidationSchema,
  validationSchema,
} from './schemas';

function normalizeCredentials(v, data) {
  if (!v && data?.length === 1) {
    return data[0].id;
  }
  return v;
}

const CampaignBasedLookalikeAudienceForm = withForm({
  validationSchema: validationSchema,
  enableReinitialize: true,
})(({
  children,
  title,
  onClose,
  onExited,
  open,
  loading,
  values,
  hierarchyState,
}) => {
  const partner = values[PARTNER];
  const type = values[TYPE];
  const credentialsCount = hierarchyState[CREDENTIALS]?.data?.length;

  const FormGroupComponent = useMemo(
    () => getCurrentComponent(values),
    [partner, type]
  );

  const targetingValidationSchema = useMemo(
    () =>
      baseCampaignBasedLookalikeTargetingValidationSchema.concat(
        getCurrentValidationSchema(values)
      ),
    [partner, type]
  );

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} />
      <SidePageV2.NewBody>
        <Section title={'General'}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField.TextField
                padding={0}
                label={'Name'}
                name={`${TARGETING}.${NAME}`}
                required={true}
              />
            </Grid>
            <Grid item xs={6}>
              <FormField.Select
                label={'Partner'}
                name={PARTNER}
                source={loadPartners}
                depends={[TYPE]}
                padding={0}
                disabled
                required={true}
              />
            </Grid>
            <FormFieldGroup
              name={TARGETING}
              validationSchema={targetingValidationSchema}
            >
              <Grid item xs={6}>
                <FormField.Select
                  name={CREDENTIALS}
                  source={loadCredentials}
                  label={'Credentials'}
                  placeholder={'Select credentials'}
                  padding={0}
                  depends={[PARTNER]}
                  required={!!credentialsCount}
                  disabled={!credentialsCount || credentialsCount <= 1}
                  normalizeValue={normalizeCredentials}
                />
              </Grid>
              <Grid item xs={6}>
                <FormField.Select
                  padding={0}
                  label={'Partner account'}
                  name={ACCOUNT}
                  required={true}
                  depends={[PARTNER, CREDENTIALS]}
                  source={loadAccounts}
                />
              </Grid>
              <Grid item xs={6}>
                <FormField.MultiSelect
                  padding={0}
                  label={'Partner campaigns'}
                  name={CAMPAIGNS}
                  required={true}
                  depends={[PARTNER, `${TARGETING}.${ACCOUNT}`]}
                  active={!!values?.[TARGETING]?.[ACCOUNT]?.length}
                  source={loadCampaigns}
                  lazy={true}
                />
              </Grid>
              <Grid item xs={12}>
                <FormField.MultiSelect
                  padding={0}
                  label={'Partner adsets'}
                  active={!!values?.[TARGETING]?.[CAMPAIGNS]?.length}
                  name={ADSETS}
                  depends={[
                    PARTNER,
                    `${TARGETING}.${ACCOUNT}`,
                    `${TARGETING}.${CAMPAIGNS}`,
                  ]}
                  source={loadAdSets}
                  lazy={true}
                />
              </Grid>
            </FormFieldGroup>
          </Grid>
        </Section>
        <FormGroupComponent
          name={TARGETING}
          validationSchema={targetingValidationSchema}
        />
        <ResultFormField
          name={ENTRIES}
          depends={[TARGETING, PARTNER, TYPE]}
          active={targetingValidationSchema.isValidSync(values[TARGETING])}
        />
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {children}
    </SidePageV2>
  );
});

export default CampaignBasedLookalikeAudienceForm;
